import React from 'react';
import {
  Button,
  Container,
  Navbar,
  Nav,
  NavDropdown,
  Spinner,
} from 'react-bootstrap';
import 'bootswatch/dist/sketchy/bootstrap.min.css';
import {FaUserCircle} from 'react-icons/fa';
import {Provider, useDispatch, useSelector} from 'react-redux';
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useLocation,
} from 'react-router-dom';
import './styles/App.css';
import HelpPage from './pages/help';
import QuickStartPage from './pages/quick-start-page';
import VersionPage from './pages/version';
import HomePage from './pages/home';
import JobsPage from './pages/jobs';
import ResumesPage from './pages/resumes';
import LoginPage, {LogoutPage, SignupSuccessPage} from './pages/login';
import TestimonialsPage from './pages/testimonials';
import VerifyEmailPage, {
  ResendEmailVerificationPage,
} from './pages/verify-email';
import ChangePasswordPage from './pages/change-password';
import RequestPasswordResetPage from './pages/request-password-reset';
import ResetPasswordPage from './pages/reset-password';
import {ShowAlerts} from './libs/alert';
import {TermsAndConditionsPage, PrivacyPolicyPage} from './libs/tn-c';
import {
  CheckoutCancelledPage,
  CheckoutSucceededPage,
  PricingPage,
  createBillingPortalSession,
} from './pages/billing';
import BannedPage from './pages/banned';
import DisabledPage from './pages/disabled';
import ActivatePage from './pages/activate';
import {RequireAuth, useAuth} from './auth/auth-context';
import {initTypst} from './libs/typst';
import {appName} from './config/constants';
import {store, type AppDispatch, type RootState} from './store/index';
import {loadVersionInfo} from './store/version-slice';
import {trackPageview, canonicalPath} from './libs/analytics';

function UserNavbar() {
  const {isAuthenticated, logout} = useAuth();

  const navDropdownTitle = <FaUserCircle size={42} />;

  return (
    <>
      {isAuthenticated ? (
        <>
          <div style={{overflow: 'visible'}} className='d-inline-block'>
            <NavDropdown
              title={navDropdownTitle}
              align='end'
              id='header-user-nav-dropdown'
            >
              <NavDropdown.Item href='/change_password'>
                Change password
              </NavDropdown.Item>
              <NavDropdown.Item
                onClick={() => {
                  void createBillingPortalSession();
                }}
              >
                Manage Subscription
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item
                onClick={() => {
                  void logout();
                }}
              >
                Logout
              </NavDropdown.Item>
            </NavDropdown>
          </div>
        </>
      ) : (
        <>
          <Nav.Link as={Link} to='/signup' className='nav-signup'>
            Sign Up
          </Nav.Link>
          <Link to='/login'>
            <Button>Login</Button>
          </Link>
        </>
      )}
    </>
  );
}

function Header() {
  const {isAuthenticated} = useAuth();

  return (
    <Navbar bg='dark' data-bs-theme='dark' expand='lg' sticky='top'>
      <Container fluid className='px-3'>
        <Navbar.Brand as={Link} to='/'>
          {appName}
        </Navbar.Brand>

        <Navbar.Toggle aria-controls='main-navbar' />

        <Navbar.Collapse id='main-navbar'>
          {/* Desktop links (visible lg and up) */}
          <Nav className='me-auto d-none d-lg-flex'>
            <Nav.Link as={Link} to='/'>
              Home
            </Nav.Link>
            <Nav.Link as={Link} to='/quickstart'>
              Quick Start
            </Nav.Link>
            {isAuthenticated && (
              <Nav.Link as={Link} to='/jobs'>
                Jobs
              </Nav.Link>
            )}
            {isAuthenticated && (
              <Nav.Link as={Link} to='/resumes'>
                Resumes
              </Nav.Link>
            )}
            <Nav.Link as={Link} to='/pricing'>
              Pricing
            </Nav.Link>
            <Nav.Link as={Link} to='/testimonials'>
              Testimonials
            </Nav.Link>
            {isAuthenticated && (
              <Nav.Link as={Link} to='/help'>
                Help
              </Nav.Link>
            )}
          </Nav>

          {/* Mobile links (visible below lg) — use the single Navbar.Toggle to open these */}
          <Nav className='me-auto d-lg-none'>
            <Nav.Link as={Link} to='/'>
              Home
            </Nav.Link>
            <Nav.Link as={Link} to='/quickstart'>
              Quick Start
            </Nav.Link>
            {isAuthenticated && (
              <Nav.Link as={Link} to='/jobs'>
                Jobs
              </Nav.Link>
            )}
            {isAuthenticated && (
              <Nav.Link as={Link} to='/resumes'>
                Resumes
              </Nav.Link>
            )}
            <Nav.Link as={Link} to='/pricing'>
              Pricing
            </Nav.Link>
            <Nav.Link as={Link} to='/testimonials'>
              Testimonials
            </Nav.Link>
            {isAuthenticated && (
              <Nav.Link as={Link} to='/help'>
                Help
              </Nav.Link>
            )}
          </Nav>

          {/* Right-side user nav */}
          <Nav className='ms-auto'>
            <UserNavbar />
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

function Footer() {
  const currentYear: number = new Date().getFullYear();
  const version = useSelector((state: RootState) => state.version.data);
  const isDev =
    globalThis.location?.hostname === 'localhost' ||
    globalThis.location?.hostname === '127.0.0.1';

  return (
    <Navbar bg='dark' data-bs-theme='dark' expand='lg' fixed='bottom'>
      <Container
        fluid
        className='px-3 gap-3 d-flex align-items-center justify-content-between flex-nowrap overflow-auto'
      >
        <div className='d-flex align-items-center gap-2'>
          <Navbar.Text>
            © {currentYear} {appName}
          </Navbar.Text>
          {version && isDev && (
            <Navbar.Text className='text-muted d-none d-lg-inline'>
              · Build {version.buildId} ·{' '}
              {new Date(version.buildTime).toLocaleString()}
            </Navbar.Text>
          )}
        </div>

        {/* Desktop legal links (visible lg and up) */}
        <Nav className='ms-lg-auto d-none d-lg-flex'>
          <Nav.Link as={Link} to='/version'>
            Version
          </Nav.Link>
          <Nav.Link as={Link} to='/terms_and_conditions'>
            Terms and Conditions
          </Nav.Link>
          <Nav.Link as={Link} to='/privacy_policy'>
            Privacy Policy
          </Nav.Link>
        </Nav>

        {/* Mobile single dropdown for legal links (visible below lg) */}
        <Nav className='ms-lg-auto d-lg-none'>
          <NavDropdown title='More' id='mobile-more-dropdown' align='end'>
            <NavDropdown.Item as={Link} to='/version'>
              Version
            </NavDropdown.Item>
            <NavDropdown.Item as={Link} to='/terms_and_conditions'>
              Terms and Conditions
            </NavDropdown.Item>
            <NavDropdown.Item as={Link} to='/privacy_policy'>
              Privacy Policy
            </NavDropdown.Item>
          </NavDropdown>
        </Nav>
      </Container>
    </Navbar>
  );
}

function Routing() {
  return (
    <Routes>
      <Route path='/quickstart' element={<QuickStartPage />} />
      <Route path='/version' element={<VersionPage />} />
      <Route path='/' element={<HomePage />} />
      <Route path='/login' element={<LoginPage />} />
      <Route path='/signup' element={<LoginPage signupMode='standard' />} />
      <Route path='/beta_signup' element={<LoginPage signupMode='beta' />} />
      <Route
        path='/jobs/*'
        element={
          <RequireAuth>
            <JobsPage />
          </RequireAuth>
        }
      />
      <Route
        path='/resumes/*'
        element={
          <RequireAuth>
            <ResumesPage />
          </RequireAuth>
        }
      />
      {/* <Route path="/example" element={<ExamplePage />} /> */}
      <Route path='/help' element={<HelpPage />} />
      <Route path='/testimonials' element={<TestimonialsPage />} />
      <Route path='/verify_email' element={<VerifyEmailPage />} />
      <Route
        path='/resend_email_verification'
        element={
          <RequireAuth>
            <ResendEmailVerificationPage />
          </RequireAuth>
        }
      />
      <Route
        path='/change_password'
        element={
          <RequireAuth>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />
      <Route
        path='/request_password_reset'
        element={<RequestPasswordResetPage />}
      />
      <Route path='/reset_password' element={<ResetPasswordPage />} />
      <Route
        path='/terms_and_conditions'
        element={<TermsAndConditionsPage />}
      />
      <Route path='/privacy_policy' element={<PrivacyPolicyPage />} />
      <Route path='/checkout/cancelled' element={<CheckoutCancelledPage />} />
      <Route path='/checkout/success' element={<CheckoutSucceededPage />} />
      <Route path='/pricing' element={<PricingPage />} />
      <Route path='/logout' element={<LogoutPage />} />
      <Route path='/signup_success' element={<SignupSuccessPage />} />
      <Route path='*' element={<HomePage />} />
    </Routes>
  );
}

type RefreshOverlayProps = {
  show: boolean;
};

function RefreshOverlay({show}: RefreshOverlayProps) {
  if (!show) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        backdropFilter: 'blur(2px)',
      }}
      aria-live='polite'
      aria-label='Refreshing session'
      role='status'
    >
      <div className='text-center text-light'>
        <Spinner
          animation='border'
          variant='light'
          style={{width: '3rem', height: '3rem'}}
        />
        <div className='mt-3'>
          <h5 className='mb-1 text-light'>Refreshing your session…</h5>
          <div className='text-light opacity-75'>
            Please wait just a moment.
          </div>
        </div>
      </div>
    </div>
  );
}

function RouteChangeTracker() {
  const location = useLocation();
  React.useEffect(() => {
    const path = canonicalPath(location.pathname);
    trackPageview(path);
  }, [location.pathname]);
  return null;
}

function AppContent() {
  const dispatch = useDispatch<AppDispatch>();
  React.useEffect(() => {
    void dispatch(loadVersionInfo());
  }, [dispatch]);

  const {
    banned,
    disabled,
    activated,
    betaUser,
    refreshAuthLoading,
    loginLoading,
    isAuthenticated,
  } = useAuth();
  void initTypst();

  if (isAuthenticated && !loginLoading && banned) {
    return (
      <BrowserRouter>
        <Header />
        <RouteChangeTracker />
        <Routes>
          <Route path='*' element={<BannedPage />} />
        </Routes>
        <Footer />
      </BrowserRouter>
    );
  }

  if (isAuthenticated && !loginLoading && !activated && !betaUser) {
    return (
      <BrowserRouter>
        <Header />
        <RouteChangeTracker />
        <Routes>
          <Route path='/signup_success' element={<SignupSuccessPage />} />
          <Route path='*' element={<ActivatePage />} />
        </Routes>
        <Footer />
      </BrowserRouter>
    );
  }

  if (isAuthenticated && !loginLoading && disabled && !betaUser) {
    return (
      <BrowserRouter>
        <Header />
        <RouteChangeTracker />
        <Routes>
          <Route path='/signup_success' element={<SignupSuccessPage />} />
          <Route path='*' element={<DisabledPage />} />
        </Routes>
        <Footer />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Header />
      <RouteChangeTracker />

      <div>
        <ShowAlerts />
        <Routing />
      </div>

      <br />
      <br />

      <Footer />

      <RefreshOverlay show={refreshAuthLoading} />
    </BrowserRouter>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
